import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-for-new-parents-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

type ParentAge = '25' | '28' | '30' | '32' | '35' | '38' | '40';
type ParentCoverage = '250k' | '500k' | '750k' | '1m';
type ParentTerm = '20' | '25' | '30';

interface ParentResult {
  carrier: string;
  amBest: string;
  monthlyRate: number;
  rateClass: string;
  approvalTime: string;
  parentNote: string;
  badge?: string;
}

const PARENT_BASE: Record<ParentAge, Record<ParentCoverage, Record<ParentTerm, number>>> = {
  '25': { '250k': { '20': 14, '25': 17, '30': 20 }, '500k': { '20': 23, '25': 28, '30': 34 }, '750k': { '20': 32, '25': 39, '30': 47 }, '1m': { '20': 39, '25': 48, '30': 58 } },
  '28': { '250k': { '20': 16, '25': 19, '30': 23 }, '500k': { '20': 26, '25': 32, '30': 38 }, '750k': { '20': 37, '25': 45, '30': 54 }, '1m': { '20': 44, '25': 54, '30': 65 } },
  '30': { '250k': { '20': 19, '25': 23, '30': 28 }, '500k': { '20': 28, '25': 35, '30': 43 }, '750k': { '20': 40, '25': 50, '30': 61 }, '1m': { '20': 48, '25': 60, '30': 73 } },
  '32': { '250k': { '20': 21, '25': 26, '30': 32 }, '500k': { '20': 32, '25': 40, '30': 49 }, '750k': { '20': 46, '25': 57, '30': 70 }, '1m': { '20': 55, '25': 68, '30': 83 } },
  '35': { '250k': { '20': 24, '25': 30, '30': 37 }, '500k': { '20': 36, '25': 46, '30': 57 }, '750k': { '20': 52, '25': 66, '30': 82 }, '1m': { '20': 64, '25': 81, '30': 100 } },
  '38': { '250k': { '20': 30, '25': 38, '30': 47 }, '500k': { '20': 46, '25': 58, '30': 72 }, '750k': { '20': 66, '25': 83, '30': 103 }, '1m': { '20': 80, '25': 101, '30': 125 } },
  '40': { '250k': { '20': 36, '25': 46, '30': 57 }, '500k': { '20': 55, '25': 70, '30': 87 }, '750k': { '20': 79, '25': 101, '30': 125 }, '1m': { '20': 97, '25': 123, '30': 153 } },
};

function getParentResults(age: ParentAge, coverage: ParentCoverage, term: ParentTerm): ParentResult[] {
  const base = PARENT_BASE[age][coverage][term];
  return [
    {
      carrier: 'Banner Life',
      amBest: 'A+',
      monthlyRate: base,
      rateClass: 'Preferred Plus',
      approvalTime: '24–48 hrs',
      parentNote: 'Best rates for new parents in Indianapolis. Accelerated underwriting — no exam up to $1M. Fastest approval for busy new parents.',
      badge: 'Best for New Parents',
    },
    {
      carrier: 'Protective Life',
      amBest: 'A+',
      monthlyRate: Math.round(base * 1.04),
      rateClass: 'Preferred Plus',
      approvalTime: '24–72 hrs',
      parentNote: 'Excellent conversion rights — convert to permanent coverage as your family grows. Strong for Indianapolis parents planning more children.',
    },
    {
      carrier: 'Pacific Life',
      amBest: 'A+',
      monthlyRate: Math.round(base * 1.07),
      rateClass: 'Preferred Plus',
      approvalTime: '1–2 weeks',
      parentNote: 'Top pick for $750K+ coverage. Competitive rates for Indianapolis professionals starting families.',
    },
    {
      carrier: 'Prudential',
      amBest: 'A+',
      monthlyRate: Math.round(base * 1.10),
      rateClass: 'Preferred',
      approvalTime: '1–3 weeks',
      parentNote: 'Best for new parents with minor health conditions. Lenient underwriting for Indianapolis parents with controlled conditions.',
    },
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      monthlyRate: Math.round(base * 1.12),
      rateClass: 'Preferred',
      approvalTime: '1–2 weeks',
      parentNote: 'Strong child rider options. Add $10K–$25K coverage for your newborn for just $5–$10/month extra.',
    },
  ];
}

const COVERAGE_CHECKLIST = [
  { item: 'Income replacement (10x annual salary)', example: '$75K salary → $750K coverage', priority: 'Critical' },
  { item: 'Mortgage payoff', example: '$250K remaining balance → add $250K', priority: 'Critical' },
  { item: 'Childcare costs (if stay-at-home parent)', example: '$25K/year × 18 years = $450K', priority: 'Critical' },
  { item: 'College education fund', example: '$100K–$200K per child', priority: 'High' },
  { item: 'Debt payoff (car, student loans, credit cards)', example: 'Add total debt balance', priority: 'High' },
  { item: 'Final expenses', example: '$15K–$25K for funeral and estate costs', priority: 'Moderate' },
];

const CLUSTER_LINKS = [
  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
  { to: '/mortgage-life-insurance-indianapolis', label: 'Mortgage Life Insurance Indianapolis', icon: 'ri-building-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/whole-life-insurance-quotes-indianapolis', label: 'Whole Life Insurance Quotes Indianapolis', icon: 'ri-shield-check-line' },
];

const FAQS = [
  {
    q: 'When should new parents in Indianapolis get life insurance?',
    a: 'New parents in Indianapolis should get life insurance as soon as possible — ideally before or immediately after the baby arrives. Here\'s why timing matters: 1. Rates are lowest when you\'re young and healthy. A 30-year-old pays $28/month for $500K coverage. A 35-year-old pays $36/month for the same coverage. Waiting 5 years costs an extra $96/year — $1,920 over a 20-year term. 2. Pregnancy can affect insurability. Some carriers rate pregnant women higher or postpone coverage until after delivery. Buying before pregnancy locks in your pre-pregnancy rate. 3. Health conditions can develop at any time. Buying now protects your insurability. The bottom line: the best time to buy life insurance as a new Indianapolis parent is right now.',
  },
  {
    q: 'How much life insurance do new parents in Indianapolis need?',
    a: 'New parents in Indianapolis typically need 10–12x their annual income in life insurance coverage. Here\'s a simple calculation for a typical Indianapolis family: Annual income: $75,000 × 10 = $750,000. Plus mortgage balance: $250,000. Plus childcare costs (if stay-at-home parent): $450,000 (18 years × $25,000/year). Plus college fund: $150,000 (2 children). Total recommended coverage: $1,000,000–$1,500,000. A $1M 20-year term policy for a healthy 30-year-old costs about $48/month — less than most car insurance payments. Both parents need coverage — not just the primary earner. The stay-at-home parent provides childcare, household management, and other services worth $25,000–$50,000/year to replace.',
  },
  {
    q: 'What is the best life insurance for new parents in Indianapolis?',
    a: 'The best life insurance for new parents in Indianapolis is term life insurance — specifically a 20–30 year term policy that covers your children through college. Here\'s why: 1. Term life is 80–90% cheaper than whole life for the same death benefit. 2. A 20–30 year term covers your children from birth through college graduation. 3. Both parents should have separate policies — don\'t rely on one joint policy. 4. The best carriers for Indianapolis new parents: Banner Life (A+, lowest rates), Protective Life (A+, best conversion rights), Pacific Life (A+, best for $750K+). 5. Consider adding a child rider — adds $10K–$25K coverage for your newborn for just $5–$10/month. An independent agent can compare all available carriers to find the best rate for your specific situation.',
  },
  {
    q: 'Can I get life insurance while pregnant in Indianapolis?',
    a: 'Yes — you can get life insurance while pregnant in Indianapolis, but there are some important considerations: First trimester (weeks 1–12): Most carriers will approve you at standard rates with no surcharge. Second trimester (weeks 13–26): Most carriers will approve you, but some may add a small surcharge or postpone until after delivery. Third trimester (weeks 27–40): Many carriers postpone coverage until 4–6 weeks after delivery. Complications (gestational diabetes, preeclampsia, etc.): May result in postponement or higher rates. The best strategy: apply for life insurance as early in pregnancy as possible — ideally in the first trimester. If you\'re already in the third trimester, apply now and the policy will take effect after delivery. Call (317) 648-2983 to discuss your specific situation.',
  },
  {
    q: 'Should both parents get life insurance in Indianapolis?',
    a: 'Yes — both parents in Indianapolis should have life insurance, including stay-at-home parents. Here\'s why: Working parent: Provides income replacement. If the working parent dies, the family loses their income. $75,000/year × 10 years = $750,000 in coverage needed. Stay-at-home parent: Provides childcare, household management, and other services. The cost to replace these services is $25,000–$50,000/year. $35,000/year × 18 years = $630,000 in coverage needed. Many Indianapolis families make the mistake of only insuring the working parent. If the stay-at-home parent dies, the surviving parent must pay for childcare, housekeeping, and other services — often $2,000–$4,000/month. A $500K 20-year term policy for a healthy 30-year-old stay-at-home parent costs about $28/month — a small price for enormous protection.',
  },
  {
    q: 'What is a child rider on a life insurance policy in Indianapolis?',
    a: 'A child rider is an add-on to your life insurance policy that provides a small death benefit for your children — typically $10,000–$25,000 per child. Key facts about child riders for Indianapolis new parents: Cost: $5–$10/month for $10,000–$25,000 coverage for all children (not per child). Coverage: Typically covers all children in the household, including future children. Age: Covers children from 15 days old to age 18–25 (depending on carrier). Conversion: Most child riders can be converted to a permanent policy when the child reaches adulthood — without a medical exam. Purpose: Covers funeral expenses and provides a small financial cushion if a child dies. Child riders are available from most major carriers including Mutual of Omaha, Protective Life, and Pacific Life. An independent agent can add a child rider to your existing policy or include it in a new policy.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Jake & Emily R.',
    location: 'Carmel, Indianapolis',
    quote: 'We had our first baby and realized we had zero life insurance. Benjamin got us both $750K 20-year term for $36/month each. We were approved in 48 hours. Our daughter is protected.',
    stars: 5,
    coverage: '$750K each, 20-year term',
    rate: '$36/month each at age 32',
  },
  {
    name: 'Sarah M.',
    location: 'Fishers, Indianapolis',
    quote: 'Stay-at-home mom with two kids. My husband had coverage but I didn\'t. Benjamin showed me I needed $600K to cover childcare costs. Got approved for $600K for $28/month. Life-changing.',
    stars: 5,
    coverage: '$600K 20-year term',
    rate: '$28/month at age 30',
  },
  {
    name: 'David & Megan K.',
    location: 'Noblesville, Indianapolis',
    quote: 'Twins on the way and no life insurance. Benjamin got us both $1M 30-year term for $48/month each. Added child riders for $8/month. Our whole family is covered for less than $110/month total.',
    stars: 5,
    coverage: '$1M each + child riders',
    rate: '$104/month total for family',
  },
  {
    name: 'Chris & Amanda T.',
    location: 'Zionsville, Indianapolis',
    quote: 'We applied while Amanda was 8 weeks pregnant. Banner Life approved us both at Preferred Plus rates before the baby arrived. Locked in the best rates before pregnancy could affect anything.',
    stars: 5,
    coverage: '$500K each, 20-year term',
    rate: 'Applied during first trimester',
  },
];

export default function LifeInsuranceForNewParentsIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [parentAge, setParentAge] = useState<ParentAge>('30');
  const [parentCoverage, setParentCoverage] = useState<ParentCoverage>('500k');
  const [parentTerm, setParentTerm] = useState<ParentTerm>('20');
  const [results, setResults] = useState<ParentResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Life Insurance for New Parents Indianapolis IN | Baby Coverage Guide | Hoosier Life Insurance';
    const description = 'Life insurance for new parents in Indianapolis, IN. How much coverage do you need? Compare rates for both parents. Child riders available. From $14/month. Call (317) 648-2983.';
    const keywords = 'life insurance for new parents Indianapolis, life insurance baby Indianapolis, life insurance young family Indianapolis, new parent life insurance Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/bcbdeb982e2d080cbee445a03dd3ecb7.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance for New Parents Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: ParentAge[] = ['25', '28', '30', '32', '35', '38', '40'];
  const coverageOptions: { value: ParentCoverage; label: string }[] = [
    { value: '250k', label: '$250K' }, { value: '500k', label: '$500K' },
    { value: '750k', label: '$750K' }, { value: '1m', label: '$1M' },
  ];
  const termOptions: { value: ParentTerm; label: string; desc: string }[] = [
    { value: '20', label: '20-Year', desc: 'Covers child to age 20–25' },
    { value: '25', label: '25-Year', desc: 'Covers child through college' },
    { value: '30', label: '30-Year', desc: 'Maximum protection period' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">Life Insurance Indianapolis</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance for New Parents</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20happy%20young%20couple%20new%20parents%20holding%20newborn%20baby%20at%20home%20warm%20cozy%20nursery%20soft%20natural%20light%20cream%20beige%20tones%20joyful%20loving%20family%20life%20insurance%20financial%20security%20photorealistic%20professional%20photography%20wide%20angle&width=1440&height=700&seq=newparent-li-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance for New Parents Indianapolis Indiana — Baby Coverage Guide"
            title="Life Insurance for New Parents in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-heart-line text-sm"></i>
              Indianapolis New Parents · Both Parents Covered · Child Riders Available · From $14/mo
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Life Insurance for New Parents —{' '}
              <span className="text-[#D97642]">Protect Your Family in Indianapolis</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Having a baby changes everything. Use our <strong className="text-white">New Parent Rate Finder</strong> to see exactly how much coverage you need and what it costs — for both parents. From just $14/month.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '$14/mo', label: 'Lowest life insurance rate for Indianapolis new parents' },
                { value: '10x', label: 'Recommended coverage — 10x annual salary for each parent' },
                { value: '48 hrs', label: 'Fastest approval — no medical exam for healthy parents' },
                { value: '$5/mo', label: 'Child rider cost — adds $10K–$25K coverage for your newborn' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#parent-finder" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-heart-line text-lg"></i>
                Find My New Parent Rate
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$14/mo', label: 'Lowest rate for Indianapolis new parents — healthy 25-year-old, $250K, 20-year term' },
              { value: '10x', label: 'Recommended coverage — 10x annual salary for each parent in Indianapolis' },
              { value: 'Both', label: 'Both parents need coverage — including stay-at-home parents' },
              { value: '$5/mo', label: 'Child rider — adds $10K–$25K coverage for your newborn for just $5/month' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">
          <div>

            {/* Coverage Checklist */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Coverage Checklist</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                What Indianapolis New Parents Need to Cover
              </h2>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="grid grid-cols-[1fr_auto_auto] bg-[#1A3A52] px-6 py-3 text-xs font-bold text-white/70 uppercase tracking-wide">
                  <span>Coverage Item</span>
                  <span className="text-center px-4">Example Amount</span>
                  <span className="text-center">Priority</span>
                </div>
                {COVERAGE_CHECKLIST.map((item, i) => (
                  <div key={i} className={`grid grid-cols-[1fr_auto_auto] px-6 py-4 border-b border-gray-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <span className="text-sm font-semibold text-[#1A3A52]">{item.item}</span>
                    <span className="text-xs text-gray-500 text-center px-4 whitespace-nowrap">{item.example}</span>
                    <span className={`text-xs font-bold text-center whitespace-nowrap ${item.priority === 'Critical' ? 'text-red-500' : item.priority === 'High' ? 'text-[#D97642]' : 'text-gray-400'}`}>{item.priority}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* NEW PARENT RATE FINDER */}
            <div id="parent-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">New Parent Rate Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Life Insurance Rates — Indianapolis New Parents
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, and term length to see rates from the top 5 carriers for Indianapolis new parents.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-heart-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setParentAge(a); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${parentAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setParentCoverage(opt.value); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${parentCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Term Length</label>
                    <div className="grid grid-cols-3 gap-3">
                      {termOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setParentTerm(opt.value); setResults(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${parentTerm === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${parentTerm === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setResults(getParentResults(parentAge, parentCoverage, parentTerm))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-heart-line text-lg"></i>
                    Find My New Parent Rate
                  </button>
                </div>

                {results !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        New Parent Rates — Age {parentAge} · {parentTerm}-Year Term
                      </h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                        From ${Math.min(...results.map(r => r.monthlyRate))}/mo
                      </span>
                    </div>
                    <div className="flex flex-col gap-4 mb-6">
                      {results.map((r, i) => (
                        <div key={r.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{r.carrier}</span>
                                  {r.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{r.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {r.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{r.parentNote}</p>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className="text-xs text-gray-400">Rate class: {r.rateClass}</span>
                                  <span className="text-xs text-gray-400">Approval: {r.approvalTime}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${r.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${(r.monthlyRate * 12).toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#D97642]/5 border border-[#D97642]/20 rounded-xl p-4 mb-5">
                      <div className="flex items-start gap-2">
                        <i className="ri-lightbulb-line text-[#D97642] text-base flex-shrink-0 mt-0.5"></i>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          <strong className="text-[#1A3A52]">Add a child rider:</strong> For just $5–$10/month, add $10,000–$25,000 in coverage for your newborn. Covers all children in the household, including future children. Can be converted to a permanent policy when your child reaches adulthood — no medical exam required.
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Protect My Family — Free 15-Min Call
                    </button>
                  </div>
                )}

                {results === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-heart-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, and term above, then click <strong className="text-[#1A3A52]">Find My New Parent Rate</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis New Parents Who Protected Their Families
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.rate}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance for New Parents Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug"><a id={`faq-newparent-indy-${i}`}>{faq.q}</a></h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-heart-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Protect Your New Family</h3>
                    <p className="text-xs text-white/70">Both parents covered · Child riders available</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
              </div>
            </div>

            {/* Sample Rates */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                New Parent Rates — $500K 20-Year Term
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: 'Age 25, $500K', rate: '$23/mo', note: 'Preferred Plus, non-smoker' },
                  { label: 'Age 28, $500K', rate: '$26/mo', note: 'Preferred Plus, non-smoker' },
                  { label: 'Age 30, $500K', rate: '$28/mo', note: 'Preferred Plus, non-smoker' },
                  { label: 'Age 32, $500K', rate: '$32/mo', note: 'Preferred Plus, non-smoker' },
                  { label: 'Age 35, $500K', rate: '$36/mo', note: 'Preferred Plus, non-smoker' },
                  { label: 'Age 38, $500K', rate: '$46/mo', note: 'Preferred Plus, non-smoker' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-xs font-semibold text-[#1A3A52]">{row.label}</div>
                      <div className="text-xs text-gray-400">{row.note}</div>
                    </div>
                    <span className="text-sm font-bold text-[#4A7C59]">{row.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Banner Life rates. Women pay ~12% less. Add child rider for $5–$10/month.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free New Parent Rate Call</h3>
                    <p className="text-xs text-white/70">Both parents covered · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free New Parent Rate Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Baby\'s age or coverage questions? (Optional)',
                    submitButton: 'Confirm My Free Rate Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Your baby changed everything — protect them today
          </h2>
          <p className="text-base text-white/75 mb-7">
            Both parents need coverage. Use our New Parent Rate Finder to see exactly what it costs — then lock in your rate before it goes up.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#parent-finder" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-heart-line text-lg"></i>
              Find My New Parent Rate
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={167} productName="Life Insurance for New Parents Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="New Parent Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Life Insurance for New Parents — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
